import { LineChartOutlined } from '@ant-design/icons'
import type { Chart } from '@antv/g2'
import { DatePicker, Typography } from 'antd'
import { last } from 'lodash'
import moment from 'moment'
import React from 'react'

import { StatisticValue, useGetStatisticsQuery } from '@/generated/graphql'

import s from './monitor.module.less'

export const MonitorWorkDayDAU = () => {
  const divId = 'dauChartDiv'

  const [yearMonth, setYearMonth] = React.useState(moment())
  const [chart, setChart] = React.useState<Chart>()

  const ref = React.useRef<HTMLDivElement>(null)

  const { data } = useGetStatisticsQuery({
    variables: {
      input: {
        year: yearMonth.year(),
        month: yearMonth.month() + 1,
      },
      withLandingPeopleCount: true,
    },
  })

  const compensateMonthData = (array: StatisticValue[]) => {
    let days = yearMonth.daysInMonth()
    const record = last(array.slice().sort((a, b) => new Date(a.date!).getTime() - new Date(b.date!).getTime()))?.date

    const date = record ? moment(record) : null
    // 不显示未来日期
    if (date && date.isBetween(moment().startOf('month'), moment().endOf('month'), 'day', '[]')) {
      days = moment().date()
    }

    return Array.from({ length: days }, (_x, index) => {
      const date = yearMonth
        .clone()
        .date(index + 1)
        .format('YYYY-MM-DD')
      const item = array.find((item) => item.date === date)
      return item || { date, value: 0 }
    }).filter((item) => ![6, 0].includes(moment(item.date).day()))
  }

  // 折线图补全所有 value 为 0 的 date
  const landingPeopleCountData = compensateMonthData(data?.statistics.landingPeopleCount || [])

  React.useEffect(() => {
    import('../chart.component').then(({ default: Chart }) => {
      const newChart = new Chart({
        container: divId,
        autoFit: true,
        syncViewPadding: true,
      })
      newChart.animate(false)
      setChart(newChart)
    })
  }, [])

  React.useEffect(() => {
    if (landingPeopleCountData && chart) {
      chart.clear()
      chart.scale({
        Date: {
          range: [0, 1],
          sync: true,
          nice: true,
        },
        DAU: {
          min: 0,
          max: 800,
        },
      })

      const view = chart.createView()
      view.data(
        landingPeopleCountData.map((row) => ({
          ...row,
          Date: row.date,
          DAU: row.value,
        }))
      )
      view.axis('Date', {
        tickLine: {
          alignTick: true,
          length: 6,
        },
      })
      view.axis('DAU', true)

      view.annotation().line({
        start: ['min', 100],
        end: ['max', 100],
        style: {
          stroke: '#ff4d4f',
          lineWidth: 1,
          lineDash: [3, 3],
        },
        text: {
          position: 'start',
          style: {
            fill: '#8c8c8c',
            fontSize: 15,
            fontWeight: 'normal',
          },
          content: '预警线 100',
          offsetY: -5,
        },
      })

      view.annotation().regionFilter({
        top: true,
        start: ['min', 100],
        end: ['max', 0],
        color: '#FF4D4F',
      })

      view.line().position('Date*DAU').label('DAU')
      view.point().position('Date*DAU')

      chart.render()
    }
  }, [chart, landingPeopleCountData])

  const header = (title: string) => (
    <div style={{ display: 'flex', fontSize: 32, margin: '16px 0 8px 0', justifyContent: 'center' }}>
      <LineChartOutlined />
      <Typography.Title style={{ marginLeft: 8 }} level={3}>
        {title}
      </Typography.Title>
    </div>
  )

  return (
    <div className={s.chartContainer}>
      {header('DAU')}
      <div className={s.actions}>
        <DatePicker value={yearMonth} onChange={(value) => value && setYearMonth(value)} picker='month' />
      </div>
      <div id={divId} className={s.chart} ref={ref} />
    </div>
  )
}
